/**
 * CortisolBreathTimer — воден дихателен таймер със звук.
 * Режими: физиологична въздишка (2 вдишвания + дълго издишване) и 4-7-8.
 * Звукът се генерира през Web Audio API, вибрацията през navigator.vibrate.
 * При завършване: POST /api/cortisol/breath-session
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wind, Play, Square, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/App";

const MODES = {
  sigh: {
    label: "Физиологична въздишка",
    hint: "Двойно вдишване през носа, дълго издишване през устата — най-бързият начин да свалиш напрежението.",
    phases: [
      { key: "in1", label: "Вдишай през носа", sec: 2.0, tone: 523 },
      { key: "in2", label: "Още едно кратко вдишване", sec: 1.0, tone: 659 },
      { key: "out", label: "Дълго издишване през устата", sec: 6.0, tone: 330 },
    ],
    cycles: 5,
  },
  m478: {
    label: "4-7-8 за вечер",
    hint: "Вдишай 4, задръж 7, издишай 8 — за заспиване и за успокояване преди сън.",
    phases: [
      { key: "in", label: "Вдишай през носа", sec: 4, tone: 523 },
      { key: "hold", label: "Задръж", sec: 7, tone: 587 },
      { key: "out", label: "Издишай през устата", sec: 8, tone: 330 },
    ],
    cycles: 4,
  },
};

const PHASE_COLOR = { in1: "#F97316", in2: "#FB923C", in: "#F97316", hold: "#A78BFA", out: "#10B981" };

export default function CortisolBreathTimer({ isDark = false, onSaved, initialMode = "sigh" }) {
  const { API } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [cycles, setCycles] = useState(5);
  const [sound, setSound] = useState(true);
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cycleNo, setCycleNo] = useState(0);
  const [left, setLeft] = useState(0);

  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const startedRef = useRef(0);

  const cfg = MODES[mode];
  const phase = cfg.phases[phaseIdx];

  useEffect(() => { setCycles(MODES[mode].cycles); }, [mode]);

  const beep = useCallback((freq, dur = 0.18) => {
    if (!sound) return;
    try {
      if (!audioRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioRef.current = new Ctx();
      }
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + dur + 0.02);
    } catch { /* тихо */ }
  }, [sound]);

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    setRunning(false);
    setPhaseIdx(0);
    setCycleNo(0);
    setLeft(0);
  }, []);

  const finish = useCallback(async (doneCycles) => {
    clearInterval(timerRef.current);
    setRunning(false);
    setPhaseIdx(0);
    setLeft(0);
    beep(784, 0.4);
    try {
      const { data } = await axios.post(`${API}/cortisol/breath-session`, {
        mode, cycles: doneCycles,
        seconds: Math.round((Date.now() - startedRef.current) / 1000),
      });
      toast.success(`${doneCycles} кръга записани — отметката за дишане е сложена`);
      onSaved?.(data);
    } catch {
      toast.error("Сесията не се записа");
    }
  }, [API, mode, beep, onSaved]);

  const start = () => {
    startedRef.current = Date.now();
    setRunning(true);
    setCycleNo(1);
    setPhaseIdx(0);
    setLeft(cfg.phases[0].sec);
    beep(cfg.phases[0].tone);
    if (navigator.vibrate) navigator.vibrate(60);

    let pi = 0;
    let cn = 1;
    let remaining = cfg.phases[0].sec;
    timerRef.current = setInterval(() => {
      remaining -= 0.1;
      if (remaining > 0.05) { setLeft(remaining); return; }
      pi += 1;
      if (pi >= cfg.phases.length) {
        pi = 0;
        cn += 1;
        if (cn > cycles) { finish(cycles); return; }
        setCycleNo(cn);
      }
      remaining = cfg.phases[pi].sec;
      setPhaseIdx(pi);
      setLeft(remaining);
      beep(cfg.phases[pi].tone);
      if (navigator.vibrate) navigator.vibrate(pi === cfg.phases.length - 1 ? [40, 40, 40] : 50);
    }, 100);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const pct = running && phase ? 1 - left / phase.sec : 0;
  const grow = phase?.key.startsWith("in");
  const scale = running ? (grow ? 0.6 + 0.4 * pct : phase?.key === "hold" ? 1 : 1 - 0.4 * pct) : 0.75;
  const color = running ? PHASE_COLOR[phase.key] : "#F97316";
  const border = isDark ? "border-white/10" : "border-slate-200";

  return (
    <div className={`rounded-2xl border p-4 ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`}
      data-testid="breath-timer-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[13px] font-extrabold inline-flex items-center gap-1.5">
          <Wind className="w-4 h-4 text-emerald-500" /> Дишане по звук
        </span>
        <button onClick={() => setSound((s) => !s)}
          className="text-[10px] opacity-70 inline-flex items-center gap-1"
          data-testid="breath-sound-toggle">
          {sound ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          {sound ? "звук вкл." : "звук изкл."}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2">
        {Object.entries(MODES).map(([k, m]) => (
          <button key={k} disabled={running} onClick={() => setMode(k)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors
              ${mode === k ? "border-emerald-500 bg-emerald-500/15 text-emerald-600" : `${border} opacity-70`}`}
            data-testid={`breath-mode-${k}`}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="text-[10px] opacity-70 mt-1.5">{cfg.hint}</div>

      <div className="flex flex-col items-center mt-3">
        <div className="relative w-40 h-40 flex items-center justify-center" data-testid="breath-visual">
          <div className="absolute rounded-full transition-transform duration-100 ease-linear"
            style={{
              width: 150, height: 150, transform: `scale(${scale})`,
              background: `radial-gradient(circle, ${color}44, ${color}11)`,
              border: `2px solid ${color}`,
            }} />
          <div className="relative text-center">
            <div className="text-[22px] font-extrabold" style={{ color }} data-testid="breath-countdown">
              {running ? Math.ceil(left) : cycles}
            </div>
            <div className="text-[10px] opacity-70 max-w-[110px]" data-testid="breath-phase">
              {running ? phase.label : "кръга"}
            </div>
          </div>
        </div>

        {running ? (
          <div className="text-[11px] opacity-80 mt-2" data-testid="breath-cycle">
            кръг {cycleNo} от {cycles}
          </div>
        ) : (
          <label className="text-[11px] opacity-80 mt-2 inline-flex items-center gap-2">
            кръгове
            <input type="range" min="1" max="12" value={cycles}
              onChange={(e) => setCycles(Number(e.target.value))}
              className="accent-emerald-500" data-testid="breath-cycles" />
            <span className="font-bold">{cycles}</span>
          </label>
        )}

        {!running ? (
          <button onClick={start}
            className="mt-3 px-4 py-2 rounded-xl text-[12px] font-bold text-white
                       bg-gradient-to-r from-emerald-500 to-teal-600 inline-flex items-center gap-1.5"
            data-testid="breath-start">
            <Play className="w-3.5 h-3.5" /> Започни
          </button>
        ) : (
          <div className="flex gap-2 mt-3">
            <button onClick={() => finish(Math.max(1, cycleNo - 1) || 1)}
              className="px-3 py-2 rounded-xl text-[12px] font-bold border border-emerald-500 text-emerald-600"
              data-testid="breath-finish">
              Запиши сесията
            </button>
            <button onClick={stop}
              className="px-3 py-2 rounded-xl text-[12px] font-bold opacity-70 border inline-flex items-center gap-1.5"
              data-testid="breath-stop">
              <Square className="w-3.5 h-3.5" /> Спри
            </button>
          </div>
        )}
      </div>

      <div className="text-[9px] opacity-50 mt-2">
        При бременност, сърдечно заболяване или епилепсия не задържайте дъха — дишайте само спокойно и равномерно.
      </div>
    </div>
  );
}
